<template>
    <Head>
        <title>Crosschain {{source}}</title>
    </Head>

    <authenticated-layout>

        <template #header>
            <v-navigation/>
        </template>

        <div class="flex flex-col bg-white p-6">

            <div class="grid grid-cols-12 p-2 items-end text-sm">
                <strong>Статус</strong>
                <strong class="col-span-3">Отправление</strong>
                <strong class="col-span-3">Получение</strong>
                <strong class="col-span-2 text-right">Комиссия наша</strong>
                <strong class="text-right">Комиссия биржевая</strong>
                <strong class="text-center">Дата создания (UTC)</strong>
            </div>

            <div v-for="r in result[source]" class="grid grid-cols-12 gap-4 border-t p-2 items-center text-sm">
                <div class="flex flex-col gap-1">
                    <span v-if="r.prevStatus" class="line-through">{{ r.prevStatus }}</span>
                    <strong>{{ r.status }}</strong>
                    <Link v-if="r.prevStatus && r.status==='ERROR'" :href="route('transactions.crosschain.retry', [source, r.id])" method="put" class="text-blue-700 hover:text-blue-900">Протолкнуть</Link>
                </div>
                <div class="col-span-3 flex flex-col text-right">
                    <v-label>Адрес отправления</v-label>
                    <span>{{ r.addressFrom }}</span>
                    <div class="flex flex-row gap-2 justify-between pt-1">
                        <span class="text-gray-500">{{ r.amountFrom }} {{ currencies[r.currencyId || r.currencyFromId] }} ({{ networks[r.networkFromId] }})</span>
                        <strong>{{ r.amountFromUSD }} $</strong>
                    </div>
                </div>
                <div class="col-span-3 flex flex-col">
                    <v-label>Адрес получения</v-label>
                    <span>{{ r.addressTo }}</span>
                    <div class="flex flex-row gap-2 justify-between pt-1">
                        <strong>{{ r.amountToUSD }} $</strong>
                        <span class="text-gray-500">{{ r.amountTo }} {{ currencies[r.currencyId || r.currencyToId] }} ({{ networks[r.networkToId] }})</span>
                    </div>
                </div>
                <div v-if="r.blockchainFee" class="col-span-2 flex flex-col gap-1 text-right">
                    <strong>{{ r.blockchainFeeUSD }} $</strong>
                    <span class="text-gray-500">{{ r.blockchainFee }} {{ currencies[r.currencyId || r.currencyFromId] }}</span>
                </div>
                <div v-else class="col-span-2 flex flex-col gap-1 text-right">
                    <strong>{{ r.blockchainFeeSource }}</strong>
                    <div class="flex flex-row gap-2 justify-end">
                        <strong>{{ r.blockchainFeeFromUSD }} $</strong>
                        <span class="text-gray-500">{{ r.blockchainFeeFrom }} {{ currencies[r.currencyId || r.currencyFromId] }}</span>
                    </div>
                    <div class="flex flex-row gap-2 justify-end">
                        <strong>{{ r.blockchainFeeToUSD }} $</strong>
                        <span class="text-gray-500">{{ r.blockchainFeeTo }} {{ currencies[r.currencyId || r.currencyToId] }}</span>
                    </div>
                </div>
                <div class="flex flex-col gap-1 text-right">
                    <strong>{{ r.serviceFeeUSD }} $</strong>
                    <span class="text-gray-500">{{ r.serviceFee }} {{ currencies[r.currencyId || r.currencyFromId] }}</span>
                </div>
                <span class="text-center">{{ moment(r.createdAt).utc().format('L LTS') }}</span>
                <div class="flex flex-row justify-around text-blue-700">
                    <Link v-if="r.userId" :href="route('ocp.user', r.userId)" title="Пользователь" class="hover:text-blue-900">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 fill-current" viewBox="0 0 16 16">
                            <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4m-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664z"/>
                        </svg>
                    </Link>
                    <Link v-if="r.organizationId" :href="route('organization', r.organizationId)" title="Организация" class="hover:text-blue-900">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 fill-current" viewBox="0 0 16 16">
                            <path d="M4 2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5zM4 5.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zM7.5 5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5zm2.5.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zM4.5 8a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5zm2.5.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5z"/>
                            <path d="M2 1a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1zm11 0H3v14h3v-2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5V15h3z"/>
                        </svg>
                    </Link>

                </div>

<!--                <pre class="text-xs col-span-full">{{r}}</pre>-->
            </div>

        </div>


    </authenticated-layout>
</template>
<script>
import {Head, Link} from '@inertiajs/inertia-vue3';
import AuthenticatedLayout from '@/Layouts/Authenticated';
import VNavigation from "@/Components/Navigation/Transactions";
import moment from "moment";
import VButton from "@/Components/Button";
import VLabel from "@/Components/Label";
import VInput from "@/Components/Input";
import OrganizationNavigation from "@/Components/Navigation/Organization.vue";

export default {
    components: {
        OrganizationNavigation,
        VNavigation,
        Head, Link,
        AuthenticatedLayout,
        VButton,
        VLabel,
        VInput,
    },
    props: {
        source: String,
        success: Boolean,
        result: Object,
        notifications: Array,
        currencies: Object,
        networks: Object,
    },
    methods: {
        moment,
    },
}
</script>
